import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  FaBed, FaBath, FaRuler, FaMapMarkerAlt, FaPhone, FaEnvelope,
  FaWhatsapp, FaChevronLeft, FaChevronRight, FaCheck, FaArrowLeft, FaExclamationCircle
} from 'react-icons/fa';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { propertyApi, formatPrice, getPropertyTypeLabel, getStatusColor } from '../services/api';
import './PropertyDetail.css';

const PropertyDetail = () => {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [currentImage, setCurrentImage] = useState(0);


  useEffect(() => {
    const load = async () => {
      try {
        const res = await propertyApi.getById(id);
        if (res.data?.data) {
          setProperty(res.data.data);
        } else {
          setNotFound(true);
        }
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id]);

  if (loading) {
    return (
      <div className="detail-page">
        <Navbar />
        <div className="detail-skeleton">
          <div className="skeleton" style={{ height: '480px', borderRadius: 0 }} />
          <div className="container" style={{ padding: '2rem 1.5rem' }}>
            <div className="skeleton" style={{ height: '40px', width: '60%', marginBottom: '1rem' }} />
            <div className="skeleton" style={{ height: '20px', width: '40%', marginBottom: '2rem' }} />
            <div className="skeleton" style={{ height: '200px' }} />
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  if (notFound || !property) {
    return (
      <div className="detail-page">
        <Navbar />
        <div className="not-found">
          <div className="not-found__icon"><FaExclamationCircle /></div>
          <h2>Propiedad no encontrada</h2>
          <p>La propiedad que buscas no existe o ya no está disponible.</p>
          <Link to="/propiedades" className="btn btn--navy">
            <FaArrowLeft /> Ver propiedades
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const images = property.imageUrls?.length > 0
    ? property.imageUrls
    : ['https://images.unsplash.com/photo-1570129477492-45c003edd2be?w=1200&auto=format'];

  const prevImage = () => setCurrentImage(i => (i - 1 + images.length) % images.length);
  const nextImage = () => setCurrentImage(i => (i + 1) % images.length);

  const whatsappMsg = encodeURIComponent(
    `Hola! Me interesa la propiedad "${property.title}" (ID: ${property.id}). ¿Está disponible?`
  );

  return (
    <div className="detail-page">
      <Navbar />

      {/* Breadcrumb */}
      <div className="breadcrumb">
        <div className="container">
          <Link to="/">Inicio</Link>
          <span>/</span>
          <Link to="/propiedades">Propiedades</Link>
          <span>/</span>
          <span>{property.title}</span>
        </div>
      </div>

      {/* Galería de imágenes */}
      <div className="detail-gallery">
        <div className="detail-gallery__main">
          <img src={images[currentImage]} alt={property.title} />
          {images.length > 1 && (
            <>
              <button className="gallery-btn gallery-btn--prev" onClick={prevImage}>
                <FaChevronLeft />
              </button>
              <button className="gallery-btn gallery-btn--next" onClick={nextImage}>
                <FaChevronRight />
              </button>
              <div className="gallery-counter">
                {currentImage + 1} / {images.length}
              </div>
            </>
          )}
          <div
            className="detail-status"
            style={{ background: getStatusColor(property.status) }}
          >
            {property.status === 'DISPONIBLE' ? 'Disponible' :
             property.status === 'VENDIDA' ? 'Vendida' :
             property.status === 'ALQUILADA' ? 'Alquilada' : 'Reservada'}
          </div>
        </div>

        {/* Thumbnails */}
        {images.length > 1 && (
          <div className="detail-gallery__thumbs">
            {images.map((img, i) => (
              <button
                key={i}
                className={`thumb ${i === currentImage ? 'thumb--active' : ''}`}
                onClick={() => setCurrentImage(i)}
              >
                <img src={img} alt={`Vista ${i + 1}`} />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Contenido principal */}
      <div className="container detail-body">
        <div className="detail-main">
          {/* Info principal */}
          <div className="detail-info">
            <div className="detail-info__type">{getPropertyTypeLabel(property.type)}</div>
            <h1 className="detail-info__title">{property.title}</h1>

            <div className="detail-info__location">
              <FaMapMarkerAlt />
              <span>{property.address}{property.city ? `, ${property.city}` : ''}{property.country ? `, ${property.country}` : ''}</span>
            </div>

            <div className="detail-info__price">
              {formatPrice(property.price, property.currency)}
            </div>

            {/* Características rápidas */}
            <div className="detail-specs">
              {property.bedrooms != null && (
                <div className="detail-spec">
                  <FaBed className="detail-spec__icon" />
                  <div>
                    <div className="detail-spec__value">{property.bedrooms}</div>
                    <div className="detail-spec__label">Habitaciones</div>
                  </div>
                </div>
              )}
              {property.bathrooms != null && (
                <div className="detail-spec">
                  <FaBath className="detail-spec__icon" />
                  <div>
                    <div className="detail-spec__value">{property.bathrooms}</div>
                    <div className="detail-spec__label">Baños</div>
                  </div>
                </div>
              )}
              {property.area != null && (
                <div className="detail-spec">
                  <FaRuler className="detail-spec__icon" />
                  <div>
                    <div className="detail-spec__value">{property.area.toLocaleString('es-SV')}</div>
                    <div className="detail-spec__label">m²</div>
                  </div>
                </div>
              )}
            </div>

            {/* Descripción */}
            <div className="detail-section">
              <h2>Descripción</h2>
              <p className="detail-description">{property.description}</p>
            </div>

            {/* Características / Amenidades */}
            {property.features?.length > 0 && (
              <div className="detail-section">
                <h2>Características y Amenidades</h2>
                <div className="detail-features">
                  {property.features.map((feature, i) => (
                    <div key={i} className="detail-feature">
                      <FaCheck className="detail-feature__icon" />
                      <span>{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}


          </div>
        </div>

        {/* Sidebar — Contacto */}
        <div className="detail-sidebar">
          <div className="contact-widget">
            <div className="contact-widget__header">
              <h3>¿Te interesa esta propiedad?</h3>
              <p>Contáctanos y con gusto te asesoramos</p>
            </div>

            <div className="contact-widget__price">{formatPrice(property.price, property.currency)}</div>

            <div className="contact-widget__actions">
              <a
                href={`https://wa.me/16474700762?text=${whatsappMsg}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn--whatsapp btn--full"
              >
                <FaWhatsapp /> WhatsApp
              </a>

              <a href="tel:+50379416355" className="btn btn--navy btn--full">
                <FaPhone /> Llamar: +503 7941-6355
              </a>

              <Link
                to={`/contacto?propertyId=${property.id}&propertyTitle=${encodeURIComponent(property.title)}`}
                className="btn btn--outline-navy btn--full"
              >
                <FaEnvelope /> Enviar mensaje
              </Link>
            </div>

            <div className="contact-widget__agents">
              <p>Nuestros agentes</p>
              <div className="agent-mini">
                <div className="agent-mini__avatar">L</div>
                <div>
                  <strong>Lilian</strong>
                  <a href="mailto:linareslilian27@gmail.com">linareslilian27@gmail.com</a>
                </div>
              </div>
              <div className="agent-mini">
                <div className="agent-mini__avatar">C</div>
                <div>
                  <strong>Carol</strong>
                  <a href="mailto:clinares79@gmail.com">clinares79@gmail.com</a>
                </div>
              </div>
            </div>
          </div>

          <Link to="/propiedades" className="back-link">
            <FaArrowLeft /> Volver a propiedades
          </Link>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default PropertyDetail;
